<template>
	<div class="sgnl-text-center">
		<v-dialog
			:model-value="dialogVisible"
			@update:model-value="closeDialog"
			:width="$vuetify.display.smAndDown ? '95%' : '1000px'"
			:height="$vuetify.display.smAndDown ? '95%' : '80vh'"
			class="sgnl-mx-0 sgnl-px-0"
			scrollable
		>
			<v-card
				color="white"
				:min-height="fullRepoList.length > 0 ? '80vh' : undefined"
			>
				<v-card-title class="dialog-header sgnl-subtitle-medium sgnl-gap-12">
					<span class="sgnl-text-truncate">Repositories on GitHub</span>
					<v-spacer />
					<v-text-field
						v-if="!$vuetify.display.mobile"
						placeholder="Search your repositories"
						:autofocus="true"
						v-model="searchQuery"
						:loading="searchingRepos"
						@input="searchUserRepositories()"
						hide-details
						class="repo-search-field"
					>
						<template #prepend-inner>
							<Search16 />
						</template>
					</v-text-field>
					<v-btn
						v-if="$vuetify.display.mobile"
						icon
						variant="text"
						size="small"
						@click="toggleSearchBar"
					>
						<Search16 />
					</v-btn>
					<v-btn
						v-if="!$vuetify.display.mobile && githubStore.githubUserDetails"
						variant="outlined"
						size="small"
					>
						<span v-if="!$vuetify.display.smAndDown">
							{{ githubStore.githubUsername }}
						</span>
						<v-avatar size="20">
							<v-img
								v-if="githubStore.githubUserDetails"
								:src="githubStore.githubUserDetails.avatar_url"
								:alt="githubStore.githubUsername"
								cover
							/>
						</v-avatar>
					</v-btn>
					<v-btn
						icon
						variant="text"
						size="small"
						@click="closeDialog"
					>
						<Close16 />
					</v-btn>
				</v-card-title>
				<!-- Mobile search bar -->
				<div
					v-if="showSearchBar"
					class="sgnl-px-24 sgnl-pb-12"
				>
					<v-text-field
						placeholder="Search your repositories"
						:autofocus="true"
						v-model="searchQuery"
						:loading="searchingRepos"
						@input="searchUserRepositories()"
						hide-details
					>
						<template #prepend-inner>
							<Search16 />
						</template>
					</v-text-field>
				</div>
				<v-divider />
				<v-card-text class="dialog-body">
					<RepoCardList
						:displayRepoList="displayRepoList"
						:showNoResultsBanner="showNoResultsBanner"
						noResultsText="No repositories matching the query were found."
						:loadingRepos="loadingRepos"
						:endOfRepoList="endOfRepoList"
						skeletonKeyPrefix="list-skeleton"
						:animateEmptyState="true"
						@repo-connection-request="requestRepoConnect"
						@repository-disconnected="repoDisconnectionEventHandler"
						@load-next-page="loadNextPage"
					/>
				</v-card-text>
				<v-divider />
			</v-card>
		</v-dialog>
	</div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import Close16 from "@carbon/icons-vue/es/close/16";
import Search16 from "@carbon/icons-vue/es/search/16";

// Utilities
import { signaloidApiClient } from "@/js/signaloidClient";

// Components
import RepoCardList from "@/components/Repositories/RepoCardList.vue";

// Stores
import { useGithubStore } from "@/stores/github";
import { useUserStore } from "@/stores/user";
import type { Endpoints } from "@octokit/types";
import { RepoConnectionConfig } from "@/types/general";

type ComponentData = {
	repositoriesPerPage: number;
	repositoriesPage: number;
	fullRepoList: Endpoints["GET /user/repos"]["response"]["data"];
	displayRepoList: Endpoints["GET /user/repos"]["response"]["data"];
	loadingRepos: boolean;
	endOfRepoList: boolean;
	searchQuery: undefined | string;
	searchingRepos: boolean;
	showSearchBarEnabled: boolean;
};

export default defineComponent({
	name: "GithubRepoListDialog",
	components: { RepoCardList, Close16, Search16 },
	props: { dialogVisible: Boolean },
	data: (): ComponentData => ({
		repositoriesPerPage: 50,
		repositoriesPage: 0,
		fullRepoList: [],
		displayRepoList: [],
		loadingRepos: false,
		endOfRepoList: false,
		searchQuery: undefined,
		searchingRepos: false,
		showSearchBarEnabled: false,
	}),
	emits: {
		"close-dialog": () => true,
		"repo-connection-request": (_data: RepoConnectionConfig) => true,
		"repository-disconnected": (_data: { repositoryID: string }) => true,
	},
	setup() {
		const githubStore = useGithubStore();
		const userStore = useUserStore();
		return { githubStore, userStore };
	},
	methods: {
		loadNextPage() {
			this.loadingRepos = true;
			this.fetchNextRepositoryBatch().then(() => {
				this.loadingRepos = false;
				this.displayRepoList = this.fullRepoList;
			});
		},
		fetchNextRepositoryBatch() {
			this.repositoriesPage += 1;

			return signaloidApiClient
				.get<Endpoints["GET /user/repos"]["response"]["data"]>(
					`proxy/github/user/repos?per_page=${this.repositoriesPerPage}&page=${this.repositoriesPage}`,
				)
				.then((response) => {
					if (response.data.length < this.repositoriesPerPage) {
						this.endOfRepoList = true;
					}
					this.fullRepoList = [...this.fullRepoList, ...response.data];
				})
				.catch((error) => console.log(error));
		},
		toggleSearchBar() {
			if (this.showSearchBarEnabled) {
				this.showSearchBarEnabled = false;
			} else {
				// if (!this.$vuetify.display.mobile) {
				this.showSearchBarEnabled = true;
				// }
			}
		},
		async fetchAllRepositories() {
			while (!this.endOfRepoList) {
				await this.fetchNextRepositoryBatch();
			}
			// console.log("all repositories fetched...");
		},
		async searchUserRepositories() {
			if (!this.searchQuery) {
				this.displayRepoList = this.fullRepoList;
			} else {
				this.searchingRepos = true;
				await this.fetchAllRepositories();

				this.displayRepoList = this.fullRepoList.filter((repo) =>
					// @ts-ignore: need to bind the "this" context correctly
					repo?.full_name.toLowerCase().includes(this.searchQuery.toLowerCase()),
				);

				this.searchingRepos = false;
			}
		},
		closeDialog() {
			this.$emit("close-dialog");
		},
		requestRepoConnect(data: RepoConnectionConfig) {
			this.$emit("repo-connection-request", data);
		},
		repoDisconnectionEventHandler(data) {
			this.$emit("repository-disconnected", data);
		},
	},
	computed: {
		showNoResultsBanner(): boolean {
			return (
				!this.loadingRepos &&
				!this.searchingRepos &&
				this.displayRepoList !== undefined &&
				this.displayRepoList.length == 0
			);
		},
		showSearchBar(): boolean {
			if (this.showSearchBarEnabled && this.$vuetify.display.mobile) {
				return true;
			} else {
				return false;
			}
		},
	},
	created() {
		this.userStore.getCurrentUserTierDetails();
	},
	mounted() {
		if (this.githubStore.githubLoggedIn) {
			this.loadNextPage();
		}
	},
});
</script>

<style lang="scss" scoped>
.repo-search-field {
	max-width: var(--sgnl-container-343);
}
</style>
